import { Check } from 'lucide-react';
import type { ForecastVariable, ForecastDaySummary } from './WeatherDashboard';
import { WEATHER_LABEL, WEATHER_MUTED, WEATHER_TITLE } from './weatherDesign';
import { WEATHER_VARIABLES } from './weatherVariables';

interface WeatherForecastHudProps {
  days: ForecastDaySummary[];
  loading: boolean;
  activeVariable: ForecastVariable;
  onVariableChange: (variable: ForecastVariable) => void;
}

function HudSkeletonPill() {
  return (
    <div
      className="flex min-w-0 flex-1 animate-pulse flex-col rounded-xl border border-gray-200 bg-gray-50 px-1.5 py-2 dark:border-gray-700 dark:bg-gray-800/80"
      aria-hidden
    >
      <div className="flex items-center gap-1">
        <span className="h-5 w-5 shrink-0 rounded-full bg-gray-200 dark:bg-gray-700" />
        <span className="h-2 w-7 rounded bg-gray-100 dark:bg-gray-800" />
      </div>
      <span className="mt-1.5 h-4 w-9 rounded bg-gray-200 dark:bg-gray-700" />
      <span className="mt-1 h-2 w-5 rounded bg-gray-100 dark:bg-gray-800" />
    </div>
  );
}

/** Variable readings — tour-style tinted cards in one row. */
export function WeatherForecastHud({
  days,
  loading,
  activeVariable,
  onVariableChange,
}: WeatherForecastHudProps) {
  const today = days[0];
  const showSkeleton = loading && days.length === 0;

  return (
    <div className="flex gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {showSkeleton
        ? WEATHER_VARIABLES.map(({ id }) => <HudSkeletonPill key={id} />)
        : WEATHER_VARIABLES.map(({ id, label, unit, icon, iconClass, pillClass, read }) => {
            const selected = activeVariable === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => onVariableChange(id)}
                aria-pressed={selected}
                aria-label={`${label} forecast${selected ? ' (map layer)' : ''}`}
                className={`relative flex min-w-[4.25rem] flex-1 flex-col rounded-xl border px-1.5 py-2 text-left shadow-sm transition-all touch-manipulation ${pillClass} ${
                  selected
                    ? 'border-emerald-400/80 ring-2 ring-emerald-500/40 dark:border-emerald-600'
                    : 'hover:brightness-[0.98] dark:hover:brightness-110'
                }`}
              >
                {selected && (
                  <span className="absolute right-1 top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-sm">
                    <Check size={8} strokeWidth={3} aria-hidden />
                  </span>
                )}
                <div className="flex items-center gap-1">
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/70 dark:bg-gray-900/40 ${iconClass}`}
                  >
                    {icon}
                  </span>
                  <span className={`whitespace-nowrap ${WEATHER_LABEL}`}>{label}</span>
                </div>
                <p className={`mt-1 text-sm font-bold tabular-nums leading-none ${WEATHER_TITLE}`}>
                  {loading ? (
                    <span className="inline-block h-3.5 w-8 animate-pulse rounded bg-gray-200/80 dark:bg-gray-700" />
                  ) : (
                    read(today)
                  )}
                </p>
                <p className={`mt-0.5 whitespace-nowrap text-[9px] ${WEATHER_MUTED}`}>{unit}</p>
              </button>
            );
          })}
    </div>
  );
}
